import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import moment from "moment";

import BreadCrumbs from "../../../components/BreadCrumbs";
import PageLoaderComponent from "../../../components/page-loader";
import {
  authApi,
  getAuthorizationHeader,
} from "../../../api-calls/user-requests";
import { ADDRESS_BOOK } from "../../../api-calls/api-urls";
import { getChipColor, stringAvatar } from "../../../utilities/commonFunctions";
import { getLanguagedataFunction } from "../../../utilities/language";
import {
  AddressHomeIcon,
  AddressLocationIcon,
  AddressPhoneIcon,
} from "../../../components/Icons";
import classes from "./address.module.css";

// The app has no pagination UI anywhere, so rather than inventing a pager this
// fetches a generous page and offers "Load more". The endpoint is properly
// paginated, so a real pager can be added later with no backend change.
const PAGE_SIZE = 50;

// getChipColor only covers pending / completed / rejected and returns
// undefined for the rest, which would strip a chip of all styling. in-progress
// and REACHED_REQUIRES_CONFIRMATION are both live statuses, so give them a
// neutral style rather than a blank one.
const FALLBACK_CHIP = {
  backgroundColor: "#EEF2F7",
  color: "#4B5563",
  padding: "13px 3px",
  fontSize: "13px",
  textTransform: "capitalize",
};

const STATUS_LABELS = {
  "in-progress": "In progress",
  REACHED_REQUIRES_CONFIRMATION: "Awaiting confirmation",
};

const AddressUsage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const ln = useSelector((state) => state.reducers.allUsers.language_json);

  const [address, setAddress] = useState(null);
  const [usages, setUsages] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    getLanguagedataFunction(dispatch);
  }, [!ln]);

  const getUsage = useCallback(
    async (nextLimit) => {
      if (!id) return;
      try {
        setLoading(true);
        const { data } = await authApi.get(
          `${ADDRESS_BOOK}/${id}/usage?page=1&limit=${nextLimit}`,
          { headers: { Authorization: getAuthorizationHeader() } }
        );
        // The entry rides along with the usage rows, so the header needs no
        // second request.
        setAddress(data?.addressBook ?? null);
        setUsages(Array.isArray(data?.usages) ? data.usages : []);
        setTotalCount(data?.totalCount ?? 0);
        setError("");
      } catch (err) {
        setError(
          err?.response?.data?.message || "Could not load usage history"
        );
      } finally {
        setLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    getUsage(limit);
  }, [getUsage, limit]);

  const handleRowClick = (row) => {
    if (!row?.slug) return;
    navigate(`/location/${row.slug.toLowerCase()}`);
  };

  const renderAssignees = (row) => {
    if (!Array.isArray(row.assignees) || row.assignees.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          Unassigned
        </Typography>
      );
    }
    return (
      <Box display="flex" alignItems="center" flexWrap="wrap" gap="8px">
        {row.assignees.map((assignee) => {
          const name = assignee?.user?.name || "Unknown";
          return (
            <Box
              key={assignee._id}
              display="flex"
              alignItems="center"
              gap="6px"
            >
              <Avatar
                {...stringAvatar(name)}
                sx={{ width: 26, height: 26, fontSize: "11px" }}
              />
              <Typography variant="body2">{name}</Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  if (!ln) return null;

  return (
    <>
      {loading && usages.length === 0 ? <PageLoaderComponent /> : null}
      <BreadCrumbs />
      <Box>
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h5" fontWeight={600} mb={1}>
            {address?.customer_name || "Customer"}
          </Typography>
          <Typography color="text.secondary" mb={2}>
            {totalCount === 0
              ? "Not used yet"
              : `Used ${totalCount} ${totalCount === 1 ? "time" : "times"}`}
          </Typography>
          <Box my={1}>
            <Typography className={classes.address_part}>
              <AddressPhoneIcon />
              {address?.mobile_number ? address.mobile_number : "n/a"}
            </Typography>
            <Typography className={classes.address_part}>
              <AddressHomeIcon />
              {address?.address ? address.address : "n/a"}
            </Typography>
            {address?.location_info?.lat ? (
              <Typography className={classes.address_part}>
                <AddressLocationIcon />
                <a
                  href={`https://www.google.com/maps?q=${address.location_info.lat},${address.location_info.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {`https://www.google.com/maps?q=${address.location_info.lat},${address.location_info.lng}`}
                </a>
              </Typography>
            ) : null}
          </Box>
        </Paper>

        <Paper sx={{ p: 2 }}>
          {error ? (
            <Typography color="error" align="center" py={2}>
              {error}
            </Typography>
          ) : null}
          <Table>
            <TableHead className={classes.address_thead}>
              <TableRow>
                <TableCell style={{ width: "18%" }}>Date Assigned</TableCell>
                <TableCell style={{ width: "30%" }}>Team Member(s)</TableCell>
                <TableCell style={{ width: "18%" }}>Status</TableCell>
                <TableCell>Task</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usages.length > 0 ? (
                usages.map((row) => (
                  <TableRow
                    key={row._id}
                    className={classes.address_tbody}
                    hover
                    sx={{ cursor: row.slug ? "pointer" : "default" }}
                    onClick={() => handleRowClick(row)}
                  >
                    <TableCell>
                      {moment(row.assigned_on || row.createdAt).format(
                        "DD MMM YYYY"
                      )}
                    </TableCell>
                    <TableCell>{renderAssignees(row)}</TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_LABELS[row.status] || row.status}
                        sx={getChipColor(row.status) || FALLBACK_CHIP}
                      />
                    </TableCell>
                    <TableCell>{row.title}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    {loading ? "Loading…" : "No Data Found"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {totalCount > usages.length ? (
            <Box display="flex" justifyContent="center" mt={2}>
              <Button
                variant="outlined"
                disabled={loading}
                onClick={() => setLimit((prev) => prev + PAGE_SIZE)}
              >
                {loading ? "Loading…" : "Load more"}
              </Button>
            </Box>
          ) : null}
        </Paper>
      </Box>
    </>
  );
};

export default AddressUsage;
